import { useState, useEffect } from 'react';
import { Box, Typography, Button, Stepper, Step, StepLabel, Paper, Stack } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import toast from 'react-hot-toast';
import { PATHS } from '@/router/paths';
import useTeacherStore from '@/store/teacherStore';
import { useSchool } from '@/hooks/useSchool';
import { Loader } from '@/components/feedback/Loader';

import { PersonalInfoStep } from './components/forms/PersonalInfoStep';
import { ProfessionalInfoStep } from './components/forms/ProfessionalInfoStep';
import { ContactInfoStep } from './components/forms/ContactInfoStep';
import { DocumentsStep } from './components/forms/DocumentsStep';
import { BankDetailsStep } from './components/forms/BankDetailsStep';
import { LoginCredentialsStep } from './components/forms/LoginCredentialsStep';

const steps = [
  'Personal Info', 
  'Professional Details', 
  'Contact Info', 
  'Documents', 
  'Bank Details',
];

const validationSchemas = [
  Yup.object({
    firstName: Yup.string()
      .trim()
      .required('First name is required')
      .matches(/^[a-zA-Z\s'-]+$/, 'First name must contain only letters'),
    lastName: Yup.string()
      .trim()
      .required('Last name is required')
      .matches(/^[a-zA-Z\s'-]+$/, 'Last name must contain only letters'),
    gender: Yup.string().required('Gender is required'),
    dateOfBirth: Yup.date().required('Date of birth is required'),
  }),
  Yup.object({
    employeeId: Yup.string().required('Employee ID is required'),
    department: Yup.string()
      .trim()
      .required('Department is required')
      .matches(/^[a-zA-Z\s.,'-]+$/, 'Department must contain only letters'),
    designation: Yup.string()
      .trim()
      .required('Designation is required')
      .matches(/^[a-zA-Z\s.,'-]+$/, 'Designation must contain only letters'),
    joiningDate: Yup.date().required('Joining date is required'),
    qualification: Yup.string().required('Qualification is required'),
  }),
  Yup.object({
    phone: Yup.string()
      .required('Phone number is required')
      .matches(/^\d{10}$/, 'Phone number must be exactly 10 digits'),
    email: Yup.string().email('Invalid email').required('Email is required'),
    address: Yup.string().required('Address is required'),
  }),
  Yup.object(), // Documents
  Yup.object(), // Bank details
];

export function EditTeacherPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { schoolId } = useSchool();
  const updateTeacher = useTeacherStore((s) => s.updateTeacher);
  const fetchTeacherById = useTeacherStore((s) => s.fetchTeacherById);
  const currentTeacher = useTeacherStore((s) => s.currentTeacher);
  const loading = useTeacherStore((s) => s.loading);
  
  const [activeStep, setActiveStep] = useState(0);
  const [initialized, setInitialized] = useState(false);
  const isLastStep = activeStep === steps.length - 1;

  useEffect(() => {
    if (schoolId && id) {
      fetchTeacherById(schoolId, id);
    }
  }, [schoolId, id, fetchTeacherById]);

  useEffect(() => {
    if (currentTeacher) {
      setInitialized(true);
    }
  }, [currentTeacher]);

  const formik = useFormik({
    initialValues: currentTeacher || {},
    enableReinitialize: true,
    validationSchema: validationSchemas[activeStep],
    onSubmit: async (values, actions) => {
      if (isLastStep) {
        try {
          await updateTeacher(schoolId, id, values);
          toast.success('Teacher updated successfully');
          navigate(`${PATHS.TEACHERS}/${id}`);
        } catch (error) {
          toast.error(error.message || 'Failed to update teacher');
          actions.setSubmitting(false);
        }
      } else {
        setActiveStep((prev) => prev + 1);
        actions.setTouched({});
        actions.setSubmitting(false);
      }
    },
  });

  if (!initialized || loading && !currentTeacher) return <Loader />;
  if (!currentTeacher) return <Typography>Teacher not found</Typography>;

  const handleBack = () => setActiveStep((prev) => prev - 1);

  const renderStepContent = (step) => {
    switch (step) {
      case 0: return <PersonalInfoStep formik={formik} />;
      case 1: return <ProfessionalInfoStep formik={formik} />;
      case 2: return <ContactInfoStep formik={formik} />;
      case 3: return <DocumentsStep formik={formik} />;
      case 4: return <BankDetailsStep formik={formik} />;
      default: return <div>Not Found</div>;
    }
  };

  return (
    <Box>
      <Box mb={4}>
        <Typography variant="h4" fontWeight={700} gutterBottom>
          Edit Teacher: {currentTeacher.firstName} {currentTeacher.lastName}
        </Typography>
        <Typography color="text.secondary">
          Update staff member information
        </Typography>
      </Box>

      <Paper sx={{ p: 4, borderRadius: 2 }}>
        <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 6 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        <form onSubmit={formik.handleSubmit}>
          <Box sx={{ minHeight: 300, mb: 4 }}>
            {renderStepContent(activeStep)}
          </Box>

          <Stack direction="row" sx={{ justifyContent: 'space-between' }}>
            <Button
              disabled={activeStep === 0 || formik.isSubmitting}
              onClick={handleBack}
              variant="outlined"
            >
              Back
            </Button>
            <Stack direction="row" spacing={2}>
              <Button onClick={() => navigate(`${PATHS.TEACHERS}/${id}`)} color="inherit">
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={formik.isSubmitting}
              >
                {isLastStep ? 'Save Changes' : 'Next Step'}
              </Button>
            </Stack>
          </Stack>
        </form>
      </Paper>
    </Box>
  );
}
